/**
 * SaveOptionsDialog Component
 *
 * Modal dialog that prompts users to choose between:
 * - Save to Draft: Update current draft without creating a version
 * - Create New Version: Save and create an immutable version snapshot
 *
 * Only shown when transaction status is 'reconciling', 'approved', or 'finalized'
 */

import { History, Loader2, Save } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import type { SaveAction } from '../../hooks/useSaveWithVersioning';
import { getVersionTypeLabel } from '../../hooks/useSaveWithVersioning';
import type { TransactionStatus } from '../../types';

export interface SaveOptionsDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Callback when the dialog should close
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Current transaction status (determines version type label)
   */
  transactionStatus: TransactionStatus;

  /**
   * Whether a save operation is in progress
   */
  isSaving: boolean;

  /**
   * Error from last save attempt
   */
  error: Error | null;

  /**
   * Callback when user confirms their save choice
   */
  onSave: (action: SaveAction, notes?: string) => Promise<void>;
}

export function SaveOptionsDialog({
  open,
  onOpenChange,
  transactionStatus,
  isSaving,
  error,
  onSave,
}: SaveOptionsDialogProps) {
  const [selectedAction, setSelectedAction] = useState<SaveAction>('save_draft');
  const [versionNotes, setVersionNotes] = useState('');

  const versionTypeLabel = getVersionTypeLabel(transactionStatus);

  const handleSave = async () => {
    try {
      await onSave(selectedAction, selectedAction === 'create_version' ? versionNotes : undefined);
      // Reset state only after successful save
      setSelectedAction('save_draft');
      setVersionNotes('');
    } catch {
      // Don't reset state on error - user can retry with same values
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSaving) {
      // Reset state when closing
      setSelectedAction('save_draft');
      setVersionNotes('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Save Changes</DialogTitle>
          <DialogDescription>
            This document is in <strong>{transactionStatus}</strong> status. Choose how you want to
            save your changes.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={selectedAction}
            onValueChange={(value) => setSelectedAction(value as SaveAction)}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3 rounded-lg border p-4 hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="save_draft" id="save_draft" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="save_draft" className="flex items-center gap-2 cursor-pointer">
                  <Save className="h-4 w-4" />
                  <span className="font-medium">Save to Draft</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Update the current working draft. No version history will be created.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 rounded-lg border p-4 hover:bg-accent/50 transition-colors">
              <RadioGroupItem value="create_version" id="create_version" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="create_version" className="flex items-center gap-2 cursor-pointer">
                  <History className="h-4 w-4" />
                  <span className="font-medium">Create {versionTypeLabel} Version</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Save changes and create an immutable version snapshot that can be restored later.
                </p>
              </div>
            </div>
          </RadioGroup>

          {selectedAction === 'create_version' && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="version-notes">Version Notes (optional)</Label>
              <Textarea
                id="version-notes"
                placeholder="Describe the changes in this version..."
                value={versionNotes}
                onChange={(e) => setVersionNotes(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error.message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : selectedAction === 'create_version' ? (
              <>
                <History className="mr-2 h-4 w-4" />
                Create Version
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Draft
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
